import { DocumentType, modelOptions, mongoose, PropType, post, pre, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import type { UnitPresetKey } from "../../Configurations/Units/UnitPresetsConfigurations.js";
import { UNIT_PRESET_KEYS, UNIT_PRESETS } from "../../Configurations/Units/UnitPresetsConfigurations.js";
import { RootLogger } from "../../logging/index.js";
import { OverWaterProtectionSettings, OverWaterProtectionSettingsSchema } from "../../settingsModels.js";
import { PinoModules } from "../../utils/index.js";
import { normalizeText, TextFoldLevel } from "../../utils/textUtils.js";
import { ObjectIdString, VerdiIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../zod-decorator/index.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { verdiIdGeneration } from "../assetIdentity/verdiIdGenerationHook.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { SensorReadingConfiguration } from "../sensorReadingConfiguration/sensorReadingConfiguration.js";
import { OrganizationFieldsWithDefaults, OrganizationSchemaTypes } from "../types/organization.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { ContactInfo } from "./subSchemas/contactInfo.js";
import { ImageOverlay } from "./subSchemas/imageOverlay.js";
import { PumpSpecifier } from "./subSchemas/pumpSpecifier.js";
import { UserSMSSettings } from "./subSchemas/userSMSSettings.js";

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

const SCHEMA_KEY = "Organization" as const;
export const ORGANIZATION_COLLECTION_NAME = "users" as const;

/** Longer than any real farm name, short enough to list several in one SMS. */
export const ORGANIZATION_NAME_MAX_LENGTH = 64;

/** Shared by DTO and Mongoose validators on {@link Organization.name}. */
export const organizationNameZodValidator = z
    .string()
    .trim()
    .min(1)
    .max(ORGANIZATION_NAME_MAX_LENGTH)
    .refine((name) => /[\p{L}\p{N}]/u.test(name), {
        message: "Organization name must include at least one letter or digit",
    });

/** The form of {@link Organization.name} that the `normalizedName` unique index compares. */
export function normalizeOrganizationName(name: string): string {
    return normalizeText(name, TextFoldLevel.SEPARATORS_WHITESPACE_PUNCTUATION_AND_CASING);
}

// Generates verdiId on "validate" rather than "save" since verdiId is a required field
@pre<Organization>("validate", async function (this: DocumentType<Organization>) {
    if (this.verdiId) return;
    try {
        this.verdiId = await verdiIdGeneration.function(AssetType.ORGANIZATION, { relatedObjectId: this._id });
    } catch (e) {
        logger.error({ error: e, organizationId: this._id }, "could not generate verdiId for organization");
        throw e;
    }
})
@post<Organization>("save", async function (this: DocumentType<Organization>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — organization saves
    // must not break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            assetCollection: ORGANIZATION_COLLECTION_NAME,
            assetType: AssetType.ORGANIZATION,
            verdiId: this.verdiId,
        })
        .catch((e) =>
            logger.warn({ error: e, verdiId: this.verdiId }, "could not sync asset identity for organization"),
        );
})
// TODO: Rename "users" collection to "organizations" in DB
@modelOptions({
    schemaOptions: {
        collection: ORGANIZATION_COLLECTION_NAME,
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<Organization>>(...addSchemaKeyHook(SCHEMA_KEY))
@pre<Organization>("save", async function saveOrganization(this: DocumentType<Organization>) {
    // Migration for image overlays
    if (!this.imageOverlays) {
        this.imageOverlays = this.ImageOverlays;
    }
    if (this.isModified("name")) {
        this.normalizedName = normalizeOrganizationName(this.name);
    }
})
@pre<Organization>(
    ["findOneAndUpdate", "updateOne", "updateMany"],
    function updateOrganizationNormalizedName(this: mongoose.Query<unknown, Organization>) {
        const update = this.getUpdate();
        if (!update || Array.isArray(update)) {
            return;
        }
        const name = update.$set?.name ?? (update as { name?: unknown }).name;
        if (typeof name === "string") {
            this.set("normalizedName", normalizeOrganizationName(name));
            return;
        }
        if (typeof update.$setOnInsert?.name === "string") {
            this.setUpdate({
                ...update,
                $setOnInsert: {
                    ...update.$setOnInsert,
                    normalizedName: normalizeOrganizationName(update.$setOnInsert.name),
                },
            });
        }
    },
)
export class Organization extends SchemaBase<Organization, OrganizationFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;
    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({
        childClass: PumpSpecifier,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => [PumpSpecifier], required: false }, PropType.ARRAY)
    public pumps?: PumpSpecifier[];

    @validate({ zodValidator: organizationNameZodValidator })
    @prop({
        type: () => String,
        required: true,
        trim: true,
        unique: true,
        validate: mongooseValidatorFromZod(organizationNameZodValidator),
    })
    public name!: string;

    /** {@link normalizeOrganizationName} of `name`, kept in step by the save and update hooks. Optional so the unique index can be sparse. */
    @validate({ zodValidator: z.string().min(1).optional(), responseOnly: true })
    @prop({ type: () => String, required: false, unique: true, sparse: true })
    public normalizedName?: string;

    @validate({ zodValidator: VerdiIdString, responseOnly: true })
    @prop({ type: () => String, required: true, unique: true })
    public verdiId!: string;

    // validated as any since ContactInfo schema is deprecated
    @validate({ zodValidator: z.any().optional() })
    @prop({ type: () => ContactInfo, required: false })
    public contactInfo?: ContactInfo;

    @validate({
        childClass: UserSMSSettings,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => UserSMSSettings, required: false })
    public SMSSettings?: UserSMSSettings;

    @validate({ zodValidator: z.enum(UNIT_PRESET_KEYS).optional() })
    @prop({ type: () => String, required: true, enum: UNIT_PRESET_KEYS, default: UNIT_PRESETS.VERDI_LEGACY.key })
    public unitPresetKey: UnitPresetKey = UNIT_PRESETS.VERDI_LEGACY.key;

    // Control whether On-Demand Irrigation feature is enabled for this org
    // hasDefault, not optional: create payloads may omit it, but Mongoose fills it so responses always carry it
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: true })
    public onDemandIrrigationEnabled: boolean = true;

    //TODO: Convert this to a properly specced prop
    @validate({ zodValidator: OverWaterProtectionSettingsSchema.partial().optional() })
    @prop({ type: () => Object, required: false })
    public defaultOverWaterProtectionSettings?: Partial<OverWaterProtectionSettings>;

    @validate({
        childClass: SensorReadingConfiguration,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => SensorReadingConfiguration, required: true, default: new SensorReadingConfiguration() })
    public sensorReadingConfiguration: SensorReadingConfiguration = new SensorReadingConfiguration();

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @prop({ type: () => Object, required: false })
    public saplingNetworkInfo?: Record<string, any>;

    /**
     * This is intentionally not strictly typed because it is for non-essential information that is saved
     * ad-hoc. Mostly legacy and unused.
     */
    @validate({ zodValidator: z.any().optional() })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public webPreferences?: Partial<{
        ActivePollPeriod: number;
        ActiveEnd: string;
        ActiveStart: string;
        InactivePollPeriod: number;
        NoInactivePolling: boolean;
        hideRedDevicesOnAdmin: boolean;
        suppressAdminSMSOnOffline: boolean;
    }>;

    @validate({
        childClass: ImageOverlay,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => [ImageOverlay], required: false }, PropType.ARRAY)
    public imageOverlays?: ImageOverlay[];

    @validate({
        childClass: VertexInfo,
        childClassTransform: (object) => object.optional(),
    })
    @prop({
        type: () => VertexInfo,
        required: true,
        default: () => new VertexInfo({ lat: 49.825171, lng: -119.464043, depth: 0 }),
    })
    public defaultCoordinates: VertexInfo = new VertexInfo({ lat: 49.825171, lng: -119.464043, depth: 0 });

    // Access related
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public sensoterraEmail?: string;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public sensoterraPassword?: string;

    /**
     * @deprecated Field renamed
     * Not sure if actually effectively migrated to new name. Should re-evaluate
     * @see imageOverlays
     */
    @validate({
        childClass: ImageOverlay,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => [ImageOverlay], required: false }, PropType.ARRAY)
    public ImageOverlays?: ImageOverlay[];

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    public adminUseOnly: boolean = false;

    @validate({ zodValidator: z.array(z.coerce.date()).optional(), responseOnly: true })
    @prop({ type: () => [Date], required: true, default: [] }, PropType.ARRAY)
    public lastSMSDates: Date[] = [];

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public spamProtectionEngaged: boolean = false;

    static getters = {
        /**
         * Returns the display name for the organization
         */
        getDisplayName: (self: Organization): string => self.name,

        /**
         * Returns whether the organization has SMS notifications configured
         */
        hasSMSNotifications: (self: Organization): boolean =>
            !!(
                self.SMSSettings &&
                Array.isArray(self.SMSSettings.phoneNumbers) &&
                self.SMSSettings.phoneNumbers.some((n) => n != null && n.length > 0)
            ),

        /**
         * Returns whether the organization has image overlays configured
         */
        hasImageOverlays: (self: Organization): boolean => !!(self.imageOverlays && self.imageOverlays.length > 0),
    } as const satisfies Record<string, (self: Organization) => any>;

    static utilities = {
        /**
         * Sanitizes an organization name by removing non-alphanumeric characters.
         * Must be a utility because we sometimes need this to query the Organization model
         */
        sanitizeUsername: (username: string): string => username.replace(/[^a-zA-Z0-9]/g, ""),

        /**
         * Checks if the current time is during active SMS hours
         */
        checkIfDuringActiveSMSHours: (self: Organization, currentTime: Date): boolean => {
            if (!self.SMSSettings) {
                return true;
            }

            return UserSMSSettings.utilities.isCurrentTimeActive(self.SMSSettings, currentTime);
        },

        /**
         * Adds an image overlay to the organization
         */
        addImageOverlay: (self: Organization, overlay: ImageOverlay): Organization => {
            const overlays = Array.isArray(self.imageOverlays) ? [...self.imageOverlays] : [];
            return {
                ...self,
                imageOverlays: [...overlays, overlay],
            };
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<OrganizationSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Organization, OrganizationFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Organization.dtoValidators = getZodSchemaFromClass(
    Organization,
) as unknown as DtoValidatorTypes<OrganizationSchemaTypes>;
